import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Spinner, Modal, Form, ListGroup } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ordersAPI, vatAPI, fulfillmentAPI, buyerAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import ThaiBahtText from 'thai-baht-text';

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [vatRate, setVatRate] = useState(7); // Default 7%
  const [fulfillmentDetails, setFulfillmentDetails] = useState(null);
  const [loadingFulfillment, setLoadingFulfillment] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [reverting, setReverting] = useState(false);

  // Tax invoice states
  const [showBuyerModal, setShowBuyerModal] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [printMode, setPrintMode] = useState('normal'); // 'normal' or 'tax'
  const [buyerSearchQuery, setBuyerSearchQuery] = useState('');
  const [buyerSuggestions, setBuyerSuggestions] = useState([]);
  const [showBuyerDropdown, setShowBuyerDropdown] = useState(false);

  // Invoice date states
  const [showDateModal, setShowDateModal] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState('');
  const [tempDate, setTempDate] = useState('');
  const [pendingPrintAction, setPendingPrintAction] = useState(null); // 'normal' or 'tax'

  // Ref to prevent duplicate API calls for the same order ID
  const loadedIdRef = useRef(null);
  const buyerSearchDebounceRef = useRef(null);

  // Helper function to get print class based on invoice mode
  // mode: 'normal' = print-only-normal (visible only when printing normal invoice)
  // mode: 'tax' = print-only-tax (visible only when printing tax invoice)
  // mode: 'both' = print-only (visible when printing any invoice type)
  const getPrintClass = (mode = 'both') => {
    if (mode === 'normal') {
      return `print-only-normal ${printMode !== 'normal' ? 'd-none' : ''}`;
    }
    if (mode === 'tax') {
      return `print-only-tax ${printMode !== 'tax' ? 'd-none' : ''}`;
    }
    return 'print-only'; // both
  };

  const loadOrder = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await ordersAPI.getById(id);
      const orderData = res.data.data;
      setOrder(orderData);

      // Load fulfillment details if order is fulfilled
      if (orderData.fulfillmentStatus === 'fulfilled') {
        loadFulfillmentDetails(id);
      } else {
        setFulfillmentDetails(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const loadFulfillmentDetails = async (orderId) => {
    try {
      setLoadingFulfillment(true);
      const res = await fulfillmentAPI.getFulfillmentDetails(orderId);
      setFulfillmentDetails(res.data.data);
    } catch (err) {
      console.error('Error loading fulfillment details:', err);
      // Don't show error to user, just log it
    } finally {
      setLoadingFulfillment(false);
    }
  };

  const loadVatRate = async () => {
    try {
      const res = await vatAPI.get();
      setVatRate(parseFloat(res.data.data.rate));
    } catch (err) {
      console.error('Error loading VAT rate:', err);
      // Keep default 7% if fails
    }
  };

  useEffect(() => {
    // Load VAT rate
    loadVatRate();

    // Only load if the ID has changed or hasn't been loaded yet
    if (loadedIdRef.current !== id) {
      loadedIdRef.current = id;
      loadOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning" text="dark">รออนุมัติ</Badge>;
      case 'approved':
        return <Badge bg="success">อนุมัติแล้ว</Badge>;
      case 'rejected':
        return <Badge bg="danger">ปฏิเสธ</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatInvoiceDate = (dateString) => {
    if (!dateString) return '________________';
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePrint = () => {
    // Show date modal first
    const today = new Date().toISOString().split('T')[0];
    setTempDate(today);
    setPendingPrintAction('normal');
    setShowDateModal(true);
  };

  // Tax invoice functions
  const handleTaxInvoiceClick = () => {
    // Show date modal first
    const today = new Date().toISOString().split('T')[0];
    setTempDate(today);
    setPendingPrintAction('tax');
    setShowDateModal(true);
  };

  const handleDateConfirm = () => {
    setInvoiceDate(tempDate);
    setShowDateModal(false);

    if (pendingPrintAction === 'normal') {
      // Print normal invoice
      setPrintMode('normal');
      setTimeout(() => window.print(), 100);
    } else if (pendingPrintAction === 'tax') {
      // Show buyer modal for tax invoice
      setShowBuyerModal(true);
      setSelectedBuyer(null);
      setBuyerSearchQuery('');
      setBuyerSuggestions([]);
      setShowBuyerDropdown(false);
    }
    setPendingPrintAction(null);
  };

  const handleDateModalClose = () => {
    setShowDateModal(false);
    setTempDate('');
    setPendingPrintAction(null);
  };

  const handleBuyerSearch = async (searchText) => {
    setBuyerSearchQuery(searchText);

    if (buyerSearchDebounceRef.current) {
      clearTimeout(buyerSearchDebounceRef.current);
    }

    if (!searchText || searchText.trim().length < 2) {
      setBuyerSuggestions([]);
      setShowBuyerDropdown(false);
      return;
    }

    buyerSearchDebounceRef.current = setTimeout(async () => {
      try {
        const res = await buyerAPI.search(searchText.trim());
        setBuyerSuggestions(res.data.data);
        setShowBuyerDropdown(res.data.data.length > 0);
      } catch (err) {
        console.error('Buyer search error:', err);
        setBuyerSuggestions([]);
        setShowBuyerDropdown(false);
      }
    }, 300);
  };

  const handleBuyerSelect = (buyer) => {
    setSelectedBuyer(buyer);
    setShowBuyerModal(false);
    setPrintMode('tax');
    setTimeout(() => window.print(), 100);
  };

  const handlePrintTaxInvoice = () => {
    if (!selectedBuyer) {
      alert('กรุณาเลือกผู้ซื้อก่อนสร้างใบกำกับภาษี');
      return;
    }
    setShowBuyerModal(false);
    setPrintMode('tax');
    setTimeout(() => window.print(), 100);
  };

  const handleCloseBuyerModal = () => {
    setShowBuyerModal(false);
    setSelectedBuyer(null);
    setBuyerSearchQuery('');
    setBuyerSuggestions([]);
    setShowBuyerDropdown(false);
  };

  const handleConfirmRevert = async () => {
    try {
      setReverting(true);
      setActionError('');
      setSuccess('');
      await fulfillmentAPI.revertToPicking(order.id);
      setShowRevertModal(false);
      setFulfillmentDetails(null);
      setSuccess('ย้อนกลับคำสั่งซื้อเป็นกำลังเบิกสำเร็จ');
      await loadOrder();
    } catch (err) {
      setActionError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการย้อนกลับคำสั่งซื้อ');
    } finally {
      setReverting(false);
    }
  };

  // Group items by category
  const groupItemsByCategory = (items) => {
    if (!items || items.length === 0) return {};

    return items.reduce((grouped, item) => {
      const category = item.material?.category || 'ไม่มีหมวดหมู่';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(item);
      return grouped;
    }, {});
  };

  const calculateItemTotal = (item) => {
    const subtotal = (item.quantity || 0) * (item.unitCost || item.material?.salePrice || 0);
    if (item.hasVat) {
      return subtotal * (1 + vatRate / 100);
    }
    return subtotal;
  };

  const getCategoryTotal = (items) => {
    return items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  const getSubtotalVat = () => {
    if (!order.items) return 0;
    return order.items.reduce((sum, item) => {
      if (item.hasVat) {
        return sum + calculateItemTotal(item);
      }
      return sum;
    }, 0);
  };

  const getSubtotalNonVat = () => {
    if (!order.items) return 0;
    return order.items.reduce((sum, item) => {
      if (!item.hasVat) {
        return sum + calculateItemTotal(item);
      }
      return sum;
    }, 0);
  };

  const getGrandTotal = () => {
    if (!order.items) return 0;
    const vatSubtotal = getSubtotalVat();
    const nonVatSubtotal = getSubtotalNonVat();
    const discountVat = order.discountVat || 0;
    const discountNonVat = order.discountNonVat || 0;

    // Ensure discounts don't exceed their respective subtotals
    const clampedDiscountVat = Math.min(discountVat, vatSubtotal);
    const clampedDiscountNonVat = Math.min(discountNonVat, nonVatSubtotal);

    return (vatSubtotal - clampedDiscountVat) + (nonVatSubtotal - clampedDiscountNonVat);
  };

  const getTotalVatAmount = () => {
    if (!order.items) return 0;
    const vatSubtotal = getSubtotalVat(); // This already includes VAT
    const discountVat = order.discountVat || 0;
    const clampedDiscountVat = Math.min(discountVat, vatSubtotal);
    const vatTotalAfterDiscount = vatSubtotal - clampedDiscountVat;

    // Calculate VAT portion from the total (which includes VAT)
    // If total = base * (1 + vatRate/100), then VAT = total * (vatRate / (100 + vatRate))
    const vatAmount = vatTotalAfterDiscount * (vatRate / (100 + vatRate));
    return vatAmount;
  };

  const getThaiTextAmount = (amount) => {
    // Handle edge cases
    if (amount === null || amount === undefined || isNaN(amount)) {
      return 'ศูนย์บาทถ้วน';
    }

    // Ensure amount is a number and handle negative numbers
    const numAmount = Math.abs(Number(amount));

    try {
      // Convert to Thai text
      return ThaiBahtText(numAmount);
    } catch (error) {
      console.error('Error converting amount to Thai text:', error);
      return 'ไม่สามารถแปลงตัวเลขได้';
    }
  };

  // Calculate how many items can fit in the blank space on first page
  const getFirstSectionItemCount = (hasBuyerDetails = false, maxItems = 25) => {
    // A4 page usable height: ~28.7cm (29.7cm - 0.5cm margins * 2)
    // Header height: ~4cm (company info + invoice title)
    // Buyer details (tax invoice only): ~2cm
    // Average row height: ~0.7cm (includes padding and borders)

    const pageHeight = 28.7; // cm
    const headerHeight = 4; // cm
    const buyerDetailsHeight = hasBuyerDetails ? 2 : 0; // cm
    const rowHeight = 0.7; // cm (approximate)
    const tableHeaderHeight = 0.8; // cm (table header row)

    // Calculate available space after header
    const availableSpace = pageHeight - headerHeight - buyerDetailsHeight - tableHeaderHeight;

    // Calculate how many rows fit
    const itemsFit = Math.floor(availableSpace / rowHeight);

    // Return at least 5 items, but cap at the specified maximum
    return Math.max(5, Math.min(itemsFit, maxItems));
  };

  if (loading) {
    return (
      <Container fluid className="main-content">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">กำลังโหลดข้อมูล...</p>
        </div>
      </Container>
    );
  }

  if (error || !order) {
    return (
      <Container fluid className="main-content">
        <Alert variant="danger">
          {error || 'ไม่พบคำสั่งซื้อที่ค้นหา'}
        </Alert>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left me-2"></i>
          กลับ
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="main-content">
      {/* Normal Invoice Header - Only visible when printing normal invoice */}
      <div className={`${getPrintClass('normal')} mb-4`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {/* Left Side - Headquarters Information */}
          <div style={{ flex: '0 0 45%' }}>
            <h3 className="fw-bold mb-3">บริษัท เดอฟอเรสต์กรุ๊ป จำกัด</h3>
            <p className="mb-1">
              <i className="bi bi-geo-alt-fill me-2"></i>
              <strong>สำนักงานใหญ่:</strong>
            </p>
            <p className="mb-1 ms-4">
              148/233 หมู่ 6 ตำบลรูสะมิแล 
              <br />
              อำเภอเมือง จังหวัดปัตตานี 94000
            </p>
            <p className="mb-1">
              <i className="bi bi-telephone-fill me-2"></i>
              <strong>โทร:</strong> 0954622792
            </p>
           
            <p className="mb-0">
              <i className="bi bi-file-text-fill me-2"></i>
              <strong>เลขประจำตัวผู้เสียภาษี:</strong> 0945564000163
            </p>
          </div>

          {/* Right Side - Invoice Details */}
          <div style={{ flex: '0 0 50%', textAlign: 'right' }}>
            <h1 className="fw-bold mb-3" style={{ fontSize: '2.5rem', letterSpacing: '2px',paddingTop: '10px' }}>
              ใบแจ้งหนี้
            </h1>
            <h2 className="fw-bold mb-3" style={{ fontSize: '2rem', letterSpacing: '1px', color: '#666' }}>
              INVOICE
            </h2>
            <div className="mb-3">
              <h5 className="mb-2">
                <i className="bi bi-building me-2"></i>
                สาขา: {order.orderedBy?.fullName || order.orderedBy?.username || '-'}
              </h5>
              <p className="mb-1">
                <strong>เลขที่ใบแจ้งหนี้:</strong> #{order.id}
              </p>
              <p className="mb-0">
                <strong>วันที่:</strong> {formatInvoiceDate(invoiceDate)}
              </p>
            </div>
          </div>
        </div>
        <hr style={{ border: '2px solid #000', margin: '20px 0' }} />
      </div>

      {/* Tax Invoice Header - Only visible when printing tax invoice */}
      <div className={`${getPrintClass('tax')} mb-4`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {/* Left Side - Headquarters Information */}
          <div style={{ flex: '0 0 45%' }}>
            <h3 className="fw-bold mb-3">บริษัท เดอฟอเรสต์กรุ๊ป จำกัด</h3>
            <p className="mb-1">
              <i className="bi bi-geo-alt-fill me-2"></i>
              <strong>สำนักงานใหญ่:</strong>
            </p>
            <p className="mb-1 ms-4">
              148/233 หมู่ 6 ตำบลรูสะมิแล
              <br />
              อำเภอเมือง จังหวัดปัตตานี 94000
            </p>
            <p className="mb-1">
              <i className="bi bi-telephone-fill me-2"></i>
              <strong>โทร:</strong> 0954622792
            </p>

            <p className="mb-0">
              <i className="bi bi-file-text-fill me-2"></i>
              <strong>เลขประจำตัวผู้เสียภาษี:</strong> 0945564000163
            </p>
          </div>

          {/* Right Side - Tax Invoice Details */}
          <div style={{ flex: '0 0 50%', textAlign: 'right' }}>
            <h1 className="fw-bold mb-3" style={{ fontSize: '2.5rem', letterSpacing: '2px',paddingTop: '10px' }}>
              ใบกำกับภาษี
            </h1>
            <h2 className="fw-bold mb-3" style={{ fontSize: '2rem', letterSpacing: '1px', color: '#666' }}>
              TAX INVOICE
            </h2>
            <div className="mb-3">
              <h5 className="mb-2">
                <i className="bi bi-building me-2"></i>
                สาขา: {order.orderedBy?.fullName || order.orderedBy?.username || '-'}
              </h5>
              <p className="mb-1">
                <strong>เลขที่ใบกำกับภาษี:</strong> #{order.id}
              </p>
              <p className="mb-0">
                <strong>วันที่:</strong> {formatInvoiceDate(invoiceDate)}
              </p>
            </div>
          </div>
        </div>
        <hr style={{ border: '2px solid #000', margin: '20px 0' }} />

        {/* Buyer Information Section */}
        {selectedBuyer && (
          <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
            <h5 className="fw-bold mb-3">
              <i className="bi bi-person-check me-2"></i>
              ข้อมูลผู้ซื้อ
            </h5>
            <div style={{ display: 'flex', gap: '30px' }}>
              <div style={{ flex: 1 }}>
                <p className="mb-2">
                  <strong>ชื่อ:</strong> {selectedBuyer.name}
                </p>
                {selectedBuyer.taxId && (
                  <p className="mb-2">
                    <strong>เลขประจำตัวผู้เสียภาษี:</strong> {selectedBuyer.taxId}
                  </p>
                )}
              </div>
              {selectedBuyer.address && (
                <div style={{ flex: 1 }}>
                  <p className="mb-0">
                    <strong>ที่อยู่:</strong>
                    <br />
                    {selectedBuyer.address}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Row className="mb-4">
        <Col>
          <Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-3 no-print">
            <i className="bi bi-arrow-left me-2"></i>
            กลับ
          </Button>
          <h2 className="screen-only">
            <i className="bi bi-receipt me-2"></i>
            รายละเอียดคำสั่งซื้อ #{order.id}
          </h2>
        </Col>
      </Row>

      {/* Action Alerts */}
      {success && (
        <Alert variant="success" className="mb-4 screen-only" dismissible onClose={() => setSuccess('')}>
          <i className="bi bi-check-circle me-2"></i>
          {success}
        </Alert>
      )}

      {actionError && (
        <Alert variant="danger" className="mb-4 screen-only" dismissible onClose={() => setActionError('')}>
          <i className="bi bi-exclamation-triangle me-2"></i>
          {actionError}
        </Alert>
      )}

      {/* Rejection Alert */}
      {order.approvalStatus === 'rejected' && order.note && (
        <Alert variant="danger" className="mb-4">
          <Alert.Heading>
            <i className="bi bi-x-circle me-2"></i>
            คำสั่งซื้อนี้ถูกปฏิเสธ
          </Alert.Heading>
          <hr />
          <p className="mb-0">
            <strong>เหตุผล:</strong> {order.note}
          </p>
          {order.approvedBy && (
            <p className="mb-0 mt-2">
              <small className="text-muted">
                ปฏิเสธโดย: {order.approvedBy.fullName || order.approvedBy.username}
                {' '}เมื่อ {formatDateTime(order.approvedAt)}
              </small>
            </p>
          )}
        </Alert>
      )}

      {/* Order Information */}
      <Row>
        <Col md={6}>
          <Card className="mb-4 screen-only">
            <Card.Header>
              <h5 className="mb-0">
                <i className="bi bi-info-circle me-2"></i>
                ข้อมูลคำสั่งซื้อ
              </h5>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col sm={4}><strong>รหัสคำสั่งซื้อ:</strong></Col>
                <Col sm={8}>#{order.id}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4}><strong>ผู้สั่ง:</strong></Col>
                <Col sm={8}>{order.orderedBy?.fullName || order.orderedBy?.username || '-'}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4}><strong>สถานะ:</strong></Col>
                <Col sm={8}>{getStatusBadge(order.approvalStatus)}</Col>
              </Row>
              <Row className="mb-2">
                <Col sm={4}><strong>วันที่สร้าง:</strong></Col>
                <Col sm={8}>{formatDateTime(order.createdAt)}</Col>
              </Row>
              {order.approvedBy && (
                <>
                  <Row className="mb-2">
                    <Col sm={4}><strong>ผู้อนุมัติ:</strong></Col>
                    <Col sm={8}>{order.approvedBy.fullName || order.approvedBy.username}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4}><strong>วันที่อนุมัติ:</strong></Col>
                    <Col sm={8}>{formatDateTime(order.approvedAt)}</Col>
                  </Row>
                </>
              )}
              {order.note && (
                <>
                  <hr />
                  <Row>
                    <Col sm={4}>
                      <strong>
                        {order.approvalStatus === 'rejected' ? (
                          <span className="text-danger">
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            เหตุผลที่ปฏิเสธ:
                          </span>
                        ) : (
                          'หมายเหตุ:'
                        )}
                      </strong>
                    </Col>
                    <Col sm={8}>
                      {order.approvalStatus === 'rejected' ? (
                        <div className="alert alert-danger py-2 mb-0">
                          <i className="bi bi-chat-left-text me-1"></i>
                          {order.note}
                        </div>
                      ) : (
                        <span>{order.note}</span>
                      )}
                    </Col>
                  </Row>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="screen-only">
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">
                <i className="bi bi-calculator me-2"></i>
                สรุปคำสั่งซื้อ
              </h5>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col sm={6}><strong>จำนวนรายการทั้งหมด:</strong></Col>
                <Col sm={6} className="text-end">
                  <Badge bg="info">{order.items?.length || 0} รายการ</Badge>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col sm={6}><strong>จำนวนชิ้นทั้งหมด:</strong></Col>
                <Col sm={6} className="text-end">
                  {order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0} ชิ้น
                </Col>
              </Row>

              <hr />

              {/* Subtotals */}
              <Row className="mb-2">
                <Col sm={6}>ยอดรวม (มี VAT):</Col>
                <Col sm={6} className="text-end">
                  {getSubtotalVat().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                </Col>
              </Row>
              <Row className="mb-2">
                <Col sm={6}>ยอดรวม (ไม่มี VAT):</Col>
                <Col sm={6} className="text-end">
                  {getSubtotalNonVat().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                </Col>
              </Row>

              {/* Show discounts if any */}
              {(order.discountVat > 0 || order.discountNonVat > 0) && (
                <>
                  <hr />
                  {order.discountVat > 0 && (
                    <Row className="mb-2">
                      <Col sm={6}>
                        <i className="bi bi-tag me-1"></i>
                        ส่วนลด (มี VAT):
                      </Col>
                      <Col sm={6} className="text-end text-danger">
                        -{order.discountVat.toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                      </Col>
                    </Row>
                  )}
                  {order.discountNonVat > 0 && (
                    <Row className="mb-2">
                      <Col sm={6}>
                        <i className="bi bi-tag me-1"></i>
                        ส่วนลด (ไม่มี VAT):
                      </Col>
                      <Col sm={6} className="text-end text-danger">
                        -{order.discountNonVat.toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                      </Col>
                    </Row>
                  )}
                </>
              )}

              <hr />

              {/* Show VAT amount if there are VAT items */}
              {getTotalVatAmount() > 0 && (
                <Row className="mb-2">
                  <Col sm={6}>
                    <i className="bi bi-receipt me-1"></i>
                    ภาษีมูลค่าเพิ่ม {vatRate}%:
                  </Col>
                  <Col sm={6} className="text-end text-success">
                    {getTotalVatAmount().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                  </Col>
                </Row>
              )}

              <hr />

              <Row>
                <Col sm={6}><h5>ราคารวมทั้งสิ้น:</h5></Col>
                <Col sm={6} className="text-end">
                  <h5 className="text-primary">
                    {getGrandTotal().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                  </h5>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Delivery Information - Only show if delivery date is set */}
          {order.delivery?.deliveryDate && (
            <Card className="mb-4 border-success">
              <Card.Header className="bg-success bg-opacity-10">
                <h5 className="mb-0 text-success">
                  <i className="bi bi-truck me-2"></i>
                  ข้อมูลการจัดส่ง
                </h5>
              </Card.Header>
              <Card.Body>
                <Row className="mb-2">
                  <Col sm={6}><strong>วันที่จัดส่ง:</strong></Col>
                  <Col sm={6} className="text-end">
                    <Badge bg="success" className="fs-6">
                      <i className="bi bi-calendar-check me-1"></i>
                      {formatDate(order.delivery.deliveryDate)}
                    </Badge>
                  </Col>
                </Row>
                {order.delivery.setBy && (
                  <Row className="mb-2">
                    <Col sm={6}><strong>กำหนดโดย:</strong></Col>
                    <Col sm={6} className="text-end">
                      {order.delivery.setBy.fullName || order.delivery.setBy.username}
                    </Col>
                  </Row>
                )}
                {order.delivery.note && (
                  <>
                    <hr />
                    <Row>
                      <Col>
                        <small className="text-muted">
                          <i className="bi bi-chat-left-text me-1"></i>
                          <strong>หมายเหตุ:</strong> {order.delivery.note}
                        </small>
                      </Col>
                    </Row>
                  </>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Fulfillment Information - Only show if order is fulfilled */}
          {order.fulfillmentStatus === 'fulfilled' && (
            <Card className="mb-4 border-primary">
              <Card.Header className="bg-primary bg-opacity-10">
                <h5 className="mb-0 text-primary">
                  <i className="bi bi-box-seam-fill me-2"></i>
                  ข้อมูลการเบิกสินค้า
                </h5>
              </Card.Header>
              <Card.Body>
                {loadingFulfillment ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" variant="primary" />
                    <p className="text-muted mt-2 mb-0">กำลังโหลดข้อมูล...</p>
                  </div>
                ) : fulfillmentDetails ? (
                  <>
                    <Row className="mb-2">
                      <Col sm={6}><strong>สถานะ:</strong></Col>
                      <Col sm={6} className="text-end">
                        <Badge bg="success">
                          <i className="bi bi-check-circle me-1"></i>
                          เบิกเสร็จสมบูรณ์
                        </Badge>
                      </Col>
                    </Row>
                    {fulfillmentDetails.fulfilledBy && (
                      <Row className="mb-2">
                        <Col sm={6}><strong>ผู้เบิก:</strong></Col>
                        <Col sm={6} className="text-end">
                          <i className="bi bi-person-check me-1"></i>
                          {fulfillmentDetails.fulfilledBy.fullName || fulfillmentDetails.fulfilledBy.username}
                        </Col>
                      </Row>
                    )}
                    {fulfillmentDetails.startedAt && (
                      <Row className="mb-2">
                        <Col sm={6}><strong>เริ่มเบิก:</strong></Col>
                        <Col sm={6} className="text-end">
                          <i className="bi bi-clock-history me-1"></i>
                          {formatDateTime(fulfillmentDetails.startedAt)}
                        </Col>
                      </Row>
                    )}
                    {fulfillmentDetails.completedAt && (
                      <Row className="mb-2">
                        <Col sm={6}><strong>เบิกเสร็จ:</strong></Col>
                        <Col sm={6} className="text-end">
                          <i className="bi bi-calendar-check me-1"></i>
                          {formatDateTime(fulfillmentDetails.completedAt)}
                        </Col>
                      </Row>
                    )}
                    {fulfillmentDetails.note && (
                      <>
                        <hr />
                        <Row>
                          <Col>
                            <small className="text-muted">
                              <i className="bi bi-chat-left-text me-1"></i>
                              <strong>หมายเหตุ:</strong> {fulfillmentDetails.note}
                            </small>
                          </Col>
                        </Row>
                      </>
                    )}
                  </>
                ) : (
                  <div className="text-center py-3">
                    <small className="text-muted">ไม่พบข้อมูลการเบิกสินค้า</small>
                  </div>
                )}
                {isAdmin() && (
                  <>
                    <hr />
                    <div className="d-grid">
                      <Button
                        variant="outline-warning"
                        onClick={() => setShowRevertModal(true)}
                        disabled={reverting}
                      >
                        <i className="bi bi-arrow-counterclockwise me-2"></i>
                        ย้อนกลับเป็นกำลังเบิก
                      </Button>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Order Items - Grouped by Category (Screen View Only) */}
      {order.items && order.items.length > 0 ? (
        <>
          {/* Screen View: Grouped by Category */}
          <div className="screen-only">
            {Object.entries(groupItemsByCategory(order.items)).map(([category, items], categoryIndex) => {
              let itemCounter = 0;
              // Calculate the starting index for this category
              const startIndex = order.items
                .slice(0, order.items.indexOf(items[0]))
                .length;

              return (
                <Card key={categoryIndex} className="mb-4">
                  <Card.Header className="bg-primary bg-opacity-10">
                    <h5 className="mb-0">
                      <i className="bi bi-tag me-2"></i>
                      {category}
                    </h5>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table responsive hover className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '6%' }} className="text-center">ลำดับ</th>
                          <th style={{ width: '10%' }}>รหัสสินค้า</th>
                          <th>ชื่อสินค้า</th>
                          <th style={{ width: '8%' }} className="text-center">จำนวน</th>
                          <th style={{ width: '10%' }} className="text-end">ราคา/หน่วย</th>
                          <th style={{ width: '6%' }} className="text-center">VAT</th>
                          <th style={{ width: '10%' }} className="text-end">รวม</th>
                          <th style={{ width: '12%' }}>หมายเหตุ</th>
                          {isAdmin() && (
                            <th style={{ width: '10%' }} className="text-center">สต็อก</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => {
                          const globalIndex = startIndex + index;
                          const unitPrice = item.unitCost || item.material?.salePrice || 0;
                          const subtotal = item.quantity * unitPrice;
                          const vatAmount = item.hasVat ? (subtotal * vatRate / 100) : 0;
                          const total = subtotal + vatAmount;

                          return (
                            <tr key={item.id}>
                              <td className="text-center">{globalIndex + 1}</td>
                              <td><code>{item.material?.code || '-'}</code></td>
                              <td>
                                <strong>{item.material?.name || '-'}</strong>
                              </td>
                              <td className="text-center">
                                {item.quantity} {item.material?.unit || ''}
                              </td>
                              <td className="text-end">
                                {unitPrice.toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿
                              </td>
                              <td className="text-center">
                                {item.hasVat ? (
                                  <Badge bg="success" title={`+${vatAmount.toFixed(2)} ฿`}>
                                    {vatRate}%
                                  </Badge>
                                ) : (
                                  <small className="text-muted">-</small>
                                )}
                              </td>
                              <td className="text-end">
                                <strong>{total.toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿</strong>
                                {item.hasVat && (
                                  <>
                                    <br />
                                    <small className="text-success">
                                      +VAT {vatAmount.toFixed(2)} ฿
                                    </small>
                                  </>
                                )}
                              </td>
                              <td><small className="text-muted">{item.note || '-'}</small></td>
                              {isAdmin() && (() => {
                                const onHand = parseFloat(item.material?.quantityOnHand ?? 0);
                                const reserved = parseFloat(item.material?.quantityReserved ?? 0);
                                const available = onHand - reserved;
                                const sufficient = available >= item.quantity;
                                return (
                                  <td className="text-center">
                                    <Badge
                                      bg={sufficient ? 'success' : 'danger'}
                                      title={`คงเหลือ: ${onHand} | จอง: ${reserved} | พร้อมจ่าย: ${available}`}
                                    >
                                      {Number.isInteger(available) ? available : available.toFixed(2)}
                                      {' '}{item.material?.unit || ''}
                                    </Badge>
                                    {!sufficient && (
                                      <div>
                                        <small className="text-danger">
                                          <i className="bi bi-exclamation-triangle me-1"></i>
                                          ขาด {(() => { const s = item.quantity - available; return Number.isInteger(s) ? s : s.toFixed(2); })()}
                                        </small>
                                      </div>
                                    )}
                                  </td>
                                );
                              })()}
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <th colSpan="6" className="text-end">รวม{category}:</th>
                          <th className="text-end">
                            {getCategoryTotal(items).toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                          </th>
                          <th></th>
                          {isAdmin() && <th></th>}
                        </tr>
                      </tfoot>
                    </Table>
                  </Card.Body>
                </Card>
              );
            })}
          </div>

          {/* Print View: Split Layout - Normal Invoice */}
          <div className={getPrintClass('normal')}>
            {(() => {
              // Calculate dynamic split point (max 25 items for normal invoice)
              const splitPoint = getFirstSectionItemCount(false, 25);
              const firstItems = order.items.slice(0, splitPoint);
              const remainingItems = order.items.slice(splitPoint);

              return (
                <div className="print-split-layout">

                  {/* First Section: Initial items that fill blank space */}
                  <div className="print-items-first-section normal-invoice">
                    <Card className="mb-0">
                      <Card.Body className="p-0">
                        <Table bordered className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: '8%' }} className="text-center">ลำดับ</th>
                              <th>ชื่อสินค้า</th>
                              <th style={{ width: '12%' }} className="text-center">จำนวน</th>
                              <th style={{ width: '15%' }} className="text-end">ราคา/หน่วย</th>
                              <th style={{ width: '8%' }} className="text-center">VAT</th>
                              <th style={{ width: '15%' }} className="text-end">รวม</th>
                              <th style={{ width: '15%' }}>หมายเหตุ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {firstItems.map((item, index) => {
                              const unitPrice = item.unitCost || item.material?.salePrice || 0;
                              const subtotal = item.quantity * unitPrice;
                              const vatAmount = item.hasVat ? (subtotal * vatRate / 100) : 0;
                              const total = subtotal + vatAmount;

                              return (
                                <tr key={item.id}>
                                  <td className="text-center">{index + 1}</td>
                                  <td>
                                    <strong>{item.material?.name || '-'}</strong>
                                  </td>
                                  <td className="text-center">
                                    {item.quantity} {item.material?.unit || ''}
                                  </td>
                                  <td className="text-end">
                                    {unitPrice.toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿
                                  </td>
                                  <td className="text-center">
                                    {item.hasVat ? (
                                      <small>{vatRate}%</small>
                                    ) : (
                                      <small>-</small>
                                    )}
                                  </td>
                                  <td className="text-end">
                                    <strong>{total.toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿</strong>
                                    {item.hasVat && (
                                      <>
                                        <br />
                                        <small className="text-success">
                                          +VAT {vatAmount.toFixed(2)} ฿
                                        </small>
                                      </>
                                    )}
                                  </td>
                                  <td><small className="text-muted">{item.note || '-'}</small></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  </div>

                  {/* Second Section: Remaining items (hidden if all items fit in first section) */}
                  {remainingItems.length > 0 && (
                    <div className="print-items-second-section normal-invoice">
                      <Card className="mb-0">
                        <Card.Body className="p-0">
                          <Table bordered className="mb-0">
                            <tbody>
                              {remainingItems.map((item, index) => {
                                const unitPrice = item.unitCost || item.material?.salePrice || 0;
                                const subtotal = item.quantity * unitPrice;
                                const vatAmount = item.hasVat ? (subtotal * vatRate / 100) : 0;
                                const total = subtotal + vatAmount;
                                const globalIndex = splitPoint + index; // Adjust index for continuous numbering

                                return (
                                  <tr key={item.id}>
                                    <td className="text-center" style={{ width: '8%' }}>{globalIndex + 1}</td>
                                    <td>
                                      <strong>{item.material?.name || '-'}</strong>
                                    </td>
                                    <td className="text-center" style={{ width: '12%' }}>
                                      {item.quantity} {item.material?.unit || ''}
                                    </td>
                                    <td className="text-end" style={{ width: '15%' }}>
                                      {unitPrice.toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿
                                    </td>
                                    <td className="text-center" style={{ width: '8%' }}>
                                      {item.hasVat ? (
                                        <small>{vatRate}%</small>
                                      ) : (
                                        <small>-</small>
                                      )}
                                    </td>
                                    <td className="text-end" style={{ width: '15%' }}>
                                      <strong>{total.toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿</strong>
                                      {item.hasVat && (
                                        <>
                                          <br />
                                          <small className="text-success">
                                            +VAT {vatAmount.toFixed(2)} ฿
                                          </small>
                                        </>
                                      )}
                                    </td>
                                    <td style={{ width: '15%' }}><small className="text-muted">{item.note || '-'}</small></td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    </div>
                  )}

                </div>
              );
            })()}
          </div>

          {/* Grand Total Card - Screen View */}
          <Card className="mb-4 border-primary screen-only">
            <Card.Body className="bg-primary bg-opacity-10">
              {(order.discountVat > 0 || order.discountNonVat > 0) ? (
                <>
                  {/* Show detailed breakdown if there are discounts */}
                  <Row className="mb-2">
                    <Col xs={6}>ยอดรวม (มี VAT):</Col>
                    <Col xs={6} className="text-end">
                      {getSubtotalVat().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                    </Col>
                  </Row>
                  {order.discountVat > 0 && (
                    <Row className="mb-2">
                      <Col xs={6} className="ps-4">
                        <i className="bi bi-tag me-1"></i>
                        ส่วนลด:
                      </Col>
                      <Col xs={6} className="text-end text-danger">
                        -{order.discountVat.toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                      </Col>
                    </Row>
                  )}
                  <Row className="mb-2">
                    <Col xs={6}>ยอดรวม (ไม่มี VAT):</Col>
                    <Col xs={6} className="text-end">
                      {getSubtotalNonVat().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                    </Col>
                  </Row>
                  {order.discountNonVat > 0 && (
                    <Row className="mb-2">
                      <Col xs={6} className="ps-4">
                        <i className="bi bi-tag me-1"></i>
                        ส่วนลด:
                      </Col>
                      <Col xs={6} className="text-end text-danger">
                        -{order.discountNonVat.toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                      </Col>
                    </Row>
                  )}
                  <hr className="my-2" />
                  <Row className="mb-2">
                    <Col xs={6}>ภาษีมูลค่าเพิ่ม {vatRate}% (หลังหักส่วนลด):</Col>
                    <Col xs={6} className="text-end text-success">
                      {getTotalVatAmount().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                    </Col>
                  </Row>
                  <hr className="my-2" />
                  <Row>
                    <Col xs={6}>
                      <h4 className="mb-0"><strong>ยอดรวมทั้งหมด:</strong></h4>
                    </Col>
                    <Col xs={6} className="text-end">
                      <h4 className="mb-0">
                        <span className="text-primary">
                          {getGrandTotal().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                        </span>
                      </h4>
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  {getTotalVatAmount() > 0 && (
                    <>
                      <Row className="mb-2">
                        <Col xs={6}>ภาษีมูลค่าเพิ่ม {vatRate}%:</Col>
                        <Col xs={6} className="text-end text-success">
                          {getTotalVatAmount().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                        </Col>
                      </Row>
                      <hr className="my-2" />
                    </>
                  )}
                  <Row>
                    <Col className="text-end">
                      <h4 className="mb-0">
                        <strong>ยอดรวมทั้งหมด:</strong>{' '}
                        <span className="text-primary">
                          {getGrandTotal().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                        </span>
                      </h4>
                    </Col>
                  </Row>
                </>
              )}
            </Card.Body>
          </Card>
        </>
      ) : (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              <i className="bi bi-list-ul me-2"></i>
              รายการวัตถุดิบ
            </h5>
          </Card.Header>
          <Card.Body>
            <div className="text-center py-4">
              <i className="bi bi-inbox" style={{ fontSize: '2rem', color: '#ccc' }}></i>
              <p className="text-muted mt-2">ไม่มีรายการสินค้า</p>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Grand Total Card - Print View (at the end before signature) - Detailed Summary - Normal Invoice */}
      {order.items && order.items.length > 0 && (
        <Card className={`mb-4 border-primary ${getPrintClass('normal')}`}>
          <Card.Header className="bg-primary bg-opacity-10">
            <h5 className="mb-0">
              <i className="bi bi-calculator me-2"></i>
              สรุปคำสั่งซื้อ
            </h5>
          </Card.Header>
          <Card.Body>
            {/* Item counts */}
            <Row className="mb-2">
              <Col xs={6}><strong>จำนวนรายการทั้งหมด:</strong></Col>
              <Col xs={6} className="text-end">
                {order.items?.length || 0} รายการ
              </Col>
            </Row>
            <Row className="mb-2">
              <Col xs={6}><strong>จำนวนชิ้นทั้งหมด:</strong></Col>
              <Col xs={6} className="text-end">
                {order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0} ชิ้น
              </Col>
            </Row>

            <hr />

            {/* Subtotals before discount */}
            <Row className="mb-2">
              <Col xs={6}>ยอดรวม (มี VAT) ก่อนหักส่วนลด:</Col>
              <Col xs={6} className="text-end">
                {getSubtotalVat().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
              </Col>
            </Row>

            {/* Discount for VAT items */}
            {order.discountVat > 0 && (
              <>
                <Row className="mb-2">
                  <Col xs={6} className="ps-4">
                    <i className="bi bi-tag me-1"></i>
                    ส่วนลด (สินค้ามี VAT):
                  </Col>
                  <Col xs={6} className="text-end text-danger">
                    -{order.discountVat.toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col xs={6} className="ps-4">
                    <strong>ยอดรวม (มี VAT) หลังหักส่วนลด:</strong>
                  </Col>
                  <Col xs={6} className="text-end text-success">
                    <strong>
                      {(() => {
                        const vatSubtotal = getSubtotalVat();
                        const clampedDiscount = Math.min(order.discountVat, vatSubtotal);
                        return Math.max(0, vatSubtotal - clampedDiscount).toLocaleString('th-TH', {
                          style: 'currency',
                          currency: 'THB'
                        });
                      })()}
                    </strong>
                  </Col>
                </Row>
              </>
            )}

            <Row className="mb-2 mt-3">
              <Col xs={6}>ยอดรวม (ไม่มี VAT) ก่อนหักส่วนลด:</Col>
              <Col xs={6} className="text-end">
                {getSubtotalNonVat().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
              </Col>
            </Row>

            {/* Discount for non-VAT items */}
            {order.discountNonVat > 0 && (
              <>
                <Row className="mb-2">
                  <Col xs={6} className="ps-4">
                    <i className="bi bi-tag me-1"></i>
                    ส่วนลด (สินค้าไม่มี VAT):
                  </Col>
                  <Col xs={6} className="text-end text-danger">
                    -{order.discountNonVat.toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col xs={6} className="ps-4">
                    <strong>ยอดรวม (ไม่มี VAT) หลังหักส่วนลด:</strong>
                  </Col>
                  <Col xs={6} className="text-end text-success">
                    <strong>
                      {(() => {
                        const nonVatSubtotal = getSubtotalNonVat();
                        const clampedDiscount = Math.min(order.discountNonVat, nonVatSubtotal);
                        return Math.max(0, nonVatSubtotal - clampedDiscount).toLocaleString('th-TH', {
                          style: 'currency',
                          currency: 'THB'
                        });
                      })()}
                    </strong>
                  </Col>
                </Row>
              </>
            )}

            <hr />

            {/* VAT Amount */}
            {getTotalVatAmount() > 0 && (
              <Row className="mb-2">
                <Col xs={6}>
                  <i className="bi bi-receipt me-1"></i>
                  <strong>ภาษีมูลค่าเพิ่ม {vatRate}%:</strong>
                </Col>
                <Col xs={6} className="text-end text-success">
                  <strong>{getTotalVatAmount().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}</strong>
                </Col>
              </Row>
            )}

            <hr />

            {/* Grand Total */}
            <Row>
              <Col xs={6}>
                <h4 className="mb-0"><strong>ราคารวมทั้งสิ้น:</strong></h4>
              </Col>
              <Col xs={6} className="text-end">
                <h4 className="mb-0">
                  <span className="text-primary">
                    {getGrandTotal().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                  </span>
                </h4>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Signature Section - Normal Invoice */}
      <div className={`${getPrintClass('normal')} mt-5`}>
        <Card>
          <Card.Body>
            <div className="mt-4" style={{ display: 'flex', gap: '30px' }}>
              <div style={{ flex: 1 }}>
                <div className="signature-box">
                  <p className="mb-5">ผู้รับ / Received by:</p>
                  <div className="mb-2" style={{ borderBottom: '1px solid #000', paddingBottom: '40px' }}></div>
                  <p className="text-center mb-1">
                    <small>( ............................................................. )</small>
                  </p>
                  <p className="text-center mb-1">
                    <small>ชื่อ-นามสกุล / Name</small>
                  </p>
                  <p className="text-center">
                    <small>วันที่ / Date: ....................................</small>
                  </p>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div className="signature-box">
                  <p className="mb-5">ผู้อนุมัติ / Approved by:</p>
                  <div className="mb-2" style={{ borderBottom: '1px solid #000', paddingBottom: '40px' }}></div>
                  <p className="text-center mb-1">
                    <small>( ............................................................. )</small>
                  </p>
                  <p className="text-center mb-1">
                    <small>ชื่อ-นามสกุล / Name</small>
                  </p>
                  <p className="text-center">
                    <small>วันที่ / Date: ....................................</small>
                  </p>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* TAX INVOICE PRINT LAYOUT - Only visible when printing tax invoice */}
      {order.items && order.items.length > 0 && (
        <>
          {/* Print View: Split Layout - Tax Invoice */}
          <div className={getPrintClass('tax')}>
            {(() => {
              // Calculate dynamic split point (max 20 items for tax invoice, considering buyer details)
              const splitPoint = getFirstSectionItemCount(!!selectedBuyer, 20);
              const firstItems = order.items.slice(0, splitPoint);
              const remainingItems = order.items.slice(splitPoint);

              return (
                <div className="print-split-layout">

                  {/* First Section: Initial items that fill blank space */}
                  <div className="print-items-first-section tax-invoice">
                    <Card className="mb-0">
                      <Card.Body className="p-0">
                        <Table bordered className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: '8%' }} className="text-center">ลำดับ</th>
                              <th>ชื่อสินค้า</th>
                              <th style={{ width: '12%' }} className="text-center">จำนวน</th>
                              <th style={{ width: '15%' }} className="text-end">ราคา/หน่วย</th>
                              <th style={{ width: '8%' }} className="text-center">VAT</th>
                              <th style={{ width: '15%' }} className="text-end">รวม</th>
                              <th style={{ width: '15%' }}>หมายเหตุ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {firstItems.map((item, index) => {
                              const unitPrice = item.unitCost || item.material?.salePrice || 0;
                              const subtotal = item.quantity * unitPrice;
                              const vatAmount = item.hasVat ? (subtotal * vatRate / 100) : 0;
                              const total = subtotal + vatAmount;

                              return (
                                <tr key={item.id}>
                                  <td className="text-center">{index + 1}</td>
                                  <td>
                                    <strong>{item.material?.name || '-'}</strong>
                                  </td>
                                  <td className="text-center">
                                    {item.quantity} {item.material?.unit || ''}
                                  </td>
                                  <td className="text-end">
                                    {unitPrice.toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿
                                  </td>
                                  <td className="text-center">
                                    {item.hasVat ? (
                                      <small>{vatRate}%</small>
                                    ) : (
                                      <small>-</small>
                                    )}
                                  </td>
                                  <td className="text-end">
                                    <strong>{total.toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿</strong>
                                    {item.hasVat && (
                                      <>
                                        <br />
                                        <small className="text-success">
                                          +VAT {vatAmount.toFixed(2)} ฿
                                        </small>
                                      </>
                                    )}
                                  </td>
                                  <td><small className="text-muted">{item.note || '-'}</small></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  </div>

                  {/* Second Section: Remaining items (hidden if all items fit in first section) */}
                  {remainingItems.length > 0 && (
                    <div className="print-items-second-section tax-invoice">
                      <Card className="mb-0">
                        <Card.Body className="p-0">
                          <Table bordered className="mb-0">
                            <tbody>
                              {remainingItems.map((item, index) => {
                                const unitPrice = item.unitCost || item.material?.salePrice || 0;
                                const subtotal = item.quantity * unitPrice;
                                const vatAmount = item.hasVat ? (subtotal * vatRate / 100) : 0;
                                const total = subtotal + vatAmount;
                                const globalIndex = splitPoint + index; // Adjust index for continuous numbering

                                return (
                                  <tr key={item.id}>
                                    <td className="text-center" style={{ width: '8%' }}>{globalIndex + 1}</td>
                                    <td>
                                      <strong>{item.material?.name || '-'}</strong>
                                    </td>
                                    <td className="text-center" style={{ width: '12%' }}>
                                      {item.quantity} {item.material?.unit || ''}
                                    </td>
                                    <td className="text-end" style={{ width: '15%' }}>
                                      {unitPrice.toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿
                                    </td>
                                    <td className="text-center" style={{ width: '8%' }}>
                                      {item.hasVat ? (
                                        <small>{vatRate}%</small>
                                      ) : (
                                        <small>-</small>
                                      )}
                                    </td>
                                    <td className="text-end" style={{ width: '15%' }}>
                                      <strong>{total.toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿</strong>
                                      {item.hasVat && (
                                        <>
                                          <br />
                                          <small className="text-success">
                                            +VAT {vatAmount.toFixed(2)} ฿
                                          </small>
                                        </>
                                      )}
                                    </td>
                                    <td style={{ width: '15%' }}><small className="text-muted">{item.note || '-'}</small></td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    </div>
                  )}

                </div>
              );
            })()}
          </div>

          {/* Grand Total Card - Tax Invoice */}
          <Card className={`mb-4 border-primary ${getPrintClass('tax')}`}>
            <Card.Header className="bg-primary bg-opacity-10">
              <h5 className="mb-0">
                <i className="bi bi-calculator me-2"></i>
                สรุปคำสั่งซื้อ
              </h5>
            </Card.Header>
            <Card.Body>
              {/* Item counts */}
              <Row className="mb-2">
                <Col xs={6}><strong>จำนวนรายการทั้งหมด:</strong></Col>
                <Col xs={6} className="text-end">
                  {order.items?.length || 0} รายการ
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs={6}><strong>จำนวนชิ้นทั้งหมด:</strong></Col>
                <Col xs={6} className="text-end">
                  {order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0} ชิ้น
                </Col>
              </Row>

              <hr />

              {/* Subtotals before discount */}
              <Row className="mb-2">
                <Col xs={6}>ยอดรวม (มี VAT) ก่อนหักส่วนลด:</Col>
                <Col xs={6} className="text-end">
                  {getSubtotalVat().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                </Col>
              </Row>

              {/* Discount for VAT items */}
              {order.discountVat > 0 && (
                <>
                  <Row className="mb-2">
                    <Col xs={6} className="ps-4">
                      <i className="bi bi-tag me-1"></i>
                      ส่วนลด (สินค้ามี VAT):
                    </Col>
                    <Col xs={6} className="text-end text-danger">
                      -{order.discountVat.toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={6} className="ps-4">
                      <strong>ยอดรวม (มี VAT) หลังหักส่วนลด:</strong>
                    </Col>
                    <Col xs={6} className="text-end text-success">
                      <strong>
                        {(() => {
                          const vatSubtotal = getSubtotalVat();
                          const clampedDiscount = Math.min(order.discountVat, vatSubtotal);
                          return Math.max(0, vatSubtotal - clampedDiscount).toLocaleString('th-TH', {
                            style: 'currency',
                            currency: 'THB'
                          });
                        })()}
                      </strong>
                    </Col>
                  </Row>
                </>
              )}

              <Row className="mb-2 mt-3">
                <Col xs={6}>ยอดรวม (ไม่มี VAT) ก่อนหักส่วนลด:</Col>
                <Col xs={6} className="text-end">
                  {getSubtotalNonVat().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                </Col>
              </Row>

              {/* Discount for non-VAT items */}
              {order.discountNonVat > 0 && (
                <>
                  <Row className="mb-2">
                    <Col xs={6} className="ps-4">
                      <i className="bi bi-tag me-1"></i>
                      ส่วนลด (สินค้าไม่มี VAT):
                    </Col>
                    <Col xs={6} className="text-end text-danger">
                      -{order.discountNonVat.toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={6} className="ps-4">
                      <strong>ยอดรวม (ไม่มี VAT) หลังหักส่วนลด:</strong>
                    </Col>
                    <Col xs={6} className="text-end text-success">
                      <strong>
                        {(() => {
                          const nonVatSubtotal = getSubtotalNonVat();
                          const clampedDiscount = Math.min(order.discountNonVat, nonVatSubtotal);
                          return Math.max(0, nonVatSubtotal - clampedDiscount).toLocaleString('th-TH', {
                            style: 'currency',
                            currency: 'THB'
                          });
                        })()}
                      </strong>
                    </Col>
                  </Row>
                </>
              )}

              <hr />

              {/* VAT Amount */}
              {getTotalVatAmount() > 0 && (
                <Row className="mb-2">
                  <Col xs={6}>
                    <i className="bi bi-receipt me-1"></i>
                    <strong>ภาษีมูลค่าเพิ่ม {vatRate}%:</strong>
                  </Col>
                  <Col xs={6} className="text-end text-success">
                    <strong>{getTotalVatAmount().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}</strong>
                  </Col>
                </Row>
              )}

              <hr />

              {/* Grand Total */}
              <Row>
                <Col xs={6}>
                  <h4 className="mb-0"><strong>ราคารวมทั้งสิ้น:</strong></h4>
                </Col>
                <Col xs={6} className="text-end">
                  <h4 className="mb-0">
                    <span className="text-primary">
                      {getGrandTotal().toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                    </span>
                  </h4>
                </Col>
              </Row>

              {/* Thai Text Representation - Tax Invoice Only */}
              <Row className="mt-3 pt-3" style={{ borderTop: '1px solid #dee2e6' }}>
                <Col xs={12}>
                  <div className="text-center p-2" style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px'
                  }}>
                    <small className="text-muted d-block mb-1">
                      จำนวนเงิน (ตัวอักษร)
                    </small>
                    <div style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      color: '#495057',
                      lineHeight: '1.6'
                    }}>
                      {getThaiTextAmount(getGrandTotal())}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Signature Section - Tax Invoice */}
          <div className={`${getPrintClass('tax')} mt-5`}>
            <Card>
              <Card.Body>
                <div className="mt-4" style={{ display: 'flex', gap: '30px' }}>
                  <div style={{ flex: 1 }}>
                    <div className="signature-box">
                      <p className="mb-5">ผู้รับ / Received by:</p>
                      <div className="mb-2" style={{ borderBottom: '1px solid #000', paddingBottom: '40px' }}></div>
                      <p className="text-center mb-1">
                        <small>( ............................................................. )</small>
                      </p>
                      <p className="text-center mb-1">
                        <small>ชื่อ-นามสกุล / Name</small>
                      </p>
                      <p className="text-center">
                        <small>วันที่ / Date: ....................................</small>
                      </p>
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="signature-box">
                      <p className="mb-5">ผู้อนุมัติ / Approved by:</p>
                      <div className="mb-2" style={{ borderBottom: '1px solid #000', paddingBottom: '40px' }}></div>
                      <p className="text-center mb-1">
                        <small>( ............................................................. )</small>
                      </p>
                      <p className="text-center mb-1">
                        <small>ชื่อ-นามสกุล / Name</small>
                      </p>
                      <p className="text-center">
                        <small>วันที่ / Date: ....................................</small>
                      </p>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </>
      )}

      {/* Action Buttons */}
      <Row className="no-print">
        <Col>
          {/* Print button - available to all users */}
          <Button
            variant="info"
            className="me-2"
            onClick={handlePrint}
          >
            <i className="bi bi-printer me-2"></i>
            พิมพ์
          </Button>

          {/* Invoice and Tax Invoice buttons - admin only */}
          {isAdmin() && (
            <>
              <Button
                variant="success"
                className="me-2"
                onClick={handlePrint}
              >
                <i className="bi bi-receipt me-2"></i>
                สร้างใบแจ้งหนี้
              </Button>
              <Button
                variant="info"
                className="me-2"
                onClick={handleTaxInvoiceClick}
              >
                <i className="bi bi-file-earmark-text me-2"></i>
                สร้างใบกำกับภาษี
              </Button>
            </>
          )}

          {/* Show edit button based on permissions */}
          {(user?.isSuperuser || order.approvalStatus === 'pending') && (
            <Link to={`/orders/${order.id}/edit`}>
              <Button variant="primary" className="me-2">
                <i className="bi bi-pencil me-2"></i>
                แก้ไขคำสั่งซื้อ
              </Button>
            </Link>
          )}
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            <i className="bi bi-arrow-left me-2"></i>
            กลับ
          </Button>
        </Col>
      </Row>

      {/* Revert Fulfillment Modal */}
      <Modal show={showRevertModal} onHide={() => !reverting && setShowRevertModal(false)} centered>
        <Modal.Header closeButton={!reverting}>
          <Modal.Title>
            <i className="bi bi-arrow-counterclockwise text-warning me-2"></i>
            ยืนยันย้อนกลับคำสั่งซื้อ
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            ต้องการย้อนกลับคำสั่งซื้อ <strong>#{order.id}</strong> เป็นสถานะกำลังเบิกใช่หรือไม่?
          </p>
          <Alert variant="warning" className="mb-0">
            <ul className="mb-0 ps-3">
              <li>จำนวนสินค้าที่เบิกแล้วจะถูกคืนกลับเข้าสต็อก</li>
              <li>จำนวนที่เบิกในรายการคำสั่งซื้อจะถูกรีเซ็ตเป็น 0</li>
              <li>คำสั่งซื้อนี้จะกลับไปปรากฏในหน้ากำลังเบิก</li>
            </ul>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRevertModal(false)} disabled={reverting}>
            ยกเลิก
          </Button>
          <Button variant="warning" onClick={handleConfirmRevert} disabled={reverting}>
            {reverting ? (
              <><span className="spinner-border spinner-border-sm me-2"></span>กำลังย้อนกลับ...</>
            ) : (
              <><i className="bi bi-arrow-counterclockwise me-2"></i>ย้อนกลับเป็นกำลังเบิก</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Date Selection Modal */}
      <Modal show={showDateModal} onHide={handleDateModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-calendar-date me-2"></i>
            เลือกวันที่สำหรับใบ{pendingPrintAction === 'tax' ? 'กำกับภาษี' : 'แจ้งหนี้'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>วันที่</Form.Label>
            <Form.Control
              type="date"
              value={tempDate}
              onChange={(e) => setTempDate(e.target.value)}
              autoFocus
            />
            <Form.Text className="text-muted">
              เลือกวันที่ที่ต้องการแสดงบนใบ{pendingPrintAction === 'tax' ? 'กำกับภาษี' : 'แจ้งหนี้'}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDateModalClose}>
            ยกเลิก
          </Button>
          <Button
            variant="primary"
            onClick={handleDateConfirm}
            disabled={!tempDate}
          >
            <i className="bi bi-check-circle me-2"></i>
            ยืนยัน
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Buyer Selection Modal for Tax Invoice */}
      <Modal show={showBuyerModal} onHide={handleCloseBuyerModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-person-check me-2"></i>
            เลือกผู้ซื้อสำหรับใบกำกับภาษี
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>ค้นหาผู้ซื้อ</Form.Label>
            <Form.Control
              type="text"
              placeholder="ค้นหาชื่อหรือเลขประจำตัวผู้เสียภาษี..."
              value={buyerSearchQuery}
              onChange={(e) => handleBuyerSearch(e.target.value)}
              autoFocus
            />
            <Form.Text className="text-muted">
              กรุณากรอกอย่างน้อย 2 ตัวอักษร
            </Form.Text>
          </Form.Group>

          {/* Buyer Search Results Dropdown */}
          {showBuyerDropdown && buyerSuggestions.length > 0 && (
            <ListGroup style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '15px' }}>
              {buyerSuggestions.map((buyer) => (
                <ListGroup.Item
                  key={buyer.id}
                  action
                  onClick={() => handleBuyerSelect(buyer)}
                  style={{ cursor: 'pointer' }}
                >
                  <div>
                    <strong>{buyer.name}</strong>
                    {buyer.taxId && (
                      <span className="text-muted ms-2">
                        (เลขประจำตัวผู้เสียภาษี: {buyer.taxId})
                      </span>
                    )}
                  </div>
                  {buyer.address && (
                    <small className="text-muted">{buyer.address}</small>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}

          {/* No results message */}
          {buyerSearchQuery.length >= 2 && !showBuyerDropdown && buyerSuggestions.length === 0 && (
            <Alert variant="info">
              <i className="bi bi-info-circle me-2"></i>
              ไม่พบข้อมูลผู้ซื้อ
            </Alert>
          )}

          {/* Selected Buyer Display */}
          {selectedBuyer && (
            <Alert variant="success">
              <h6 className="mb-2">
                <i className="bi bi-check-circle me-2"></i>
                ผู้ซื้อที่เลือก:
              </h6>
              <div><strong>ชื่อ:</strong> {selectedBuyer.name}</div>
              {selectedBuyer.address && (
                <div><strong>ที่อยู่:</strong> {selectedBuyer.address}</div>
              )}
              {selectedBuyer.taxId && (
                <div><strong>เลขประจำตัวผู้เสียภาษี:</strong> {selectedBuyer.taxId}</div>
              )}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseBuyerModal}>
            ยกเลิก
          </Button>
          <Button
            variant="info"
            onClick={handlePrintTaxInvoice}
            disabled={!selectedBuyer}
          >
            <i className="bi bi-file-earmark-text me-2"></i>
            สร้างใบกำกับภาษี
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default OrderDetail;
